import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DateRange, MatCalendar, MatCalendarView } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { add, endOfDay, isAfter, isBefore, isSameDay, startOfDay, startOfMonth, sub } from 'date-fns';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCalendar, MatIconModule, MatButtonModule, DatePipe],
  providers: [provideNativeDateAdapter()],
  selector: 'avs-calendar',
  styleUrls: ['./calendar.component.scss'],
  templateUrl: './calendar.component.html',
})
export class CalendarComponent {
  maxDate = input.required<Date>();
  minDate = input.required<Date>();
  value = input<DateRange<Date> | null>();

  range = signal(new DateRange<Date>(null, null));

  protected left = viewChild.required<MatCalendar<Date>>('left');
  protected right = viewChild.required<MatCalendar<Date>>('right');
  protected view = signal<MatCalendarView>('month');

  protected startAt = signal(sub(new Date(), { months: 1 }));
  protected endAt = computed(() => add(this.startAt(), { months: 1 }));

  protected nextMonthDisabled = computed(() =>
    isAfter(startOfMonth(add(this.endAt(), { months: 1 })), startOfMonth(this.maxDate())),
  );
  protected nextYearDisabled = computed(() =>
    isAfter(startOfMonth(add(this.endAt(), { years: 1 })), startOfMonth(this.maxDate())),
  );

  protected prevMonthDisabled = computed(() =>
    isBefore(startOfMonth(sub(this.startAt(), { months: 1 })), startOfMonth(this.minDate())),
  );
  protected prevYearDisabled = computed(() =>
    isBefore(startOfMonth(sub(this.startAt(), { years: 1 })), startOfMonth(this.minDate())),
  );

  constructor() {
    effect(() => {
      const value = this.value();

      if (value) {
        this.range.set(new DateRange<Date>(value.start, value.end));
      }
    });
  }

  reset() {
    this.startAt.set(sub(new Date(), { months: 1 }));

    this.range.set(new DateRange<Date>(null, null));
    this.view.set('month');

    this.left().activeDate = this.startAt();
    this.right().activeDate = this.endAt();
  }

  protected onMonthSelected(date: Date) {
    const isRightBoundary = isSameDay(startOfMonth(date), startOfMonth(this.maxDate()));

    if (isRightBoundary) {
      this.left().activeDate = sub(date, { months: 1 });
      this.right().activeDate = date;
    } else {
      this.left().activeDate = date;
      this.right().activeDate = add(date, { months: 1 });
    }

    this.view.set('month');
    this.startAt.set(this.left().activeDate);
  }

  protected toggleYearView() {
    this.view.update(view => view === 'multi-year' ? 'month' : 'multi-year');
  }

  protected onDateSelected(date: Date) {
    let { end, start } = this.range();

    if (!start || date.getTime() < start.getTime()) {
      start = startOfDay(date);
      end = null;
    } else if (!end && date.getTime() >= start.getTime()) {
      end = endOfDay(date);
    } else {
      start = date;
      end = null;
    }

    this.range.set(new DateRange<Date>(start, end));
  }

  protected navigate(module: 'months' | 'years', direction: number) {
    this.left().activeDate = add(this.left().activeDate, { [module]: direction });
    this.right().activeDate = add(this.right().activeDate, { [module]: direction });

    this.startAt.set(this.left().activeDate);
  }
}
